import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  WrenchIcon,
  ScanLineIcon,
  ArrowDownToLineIcon,
  CheckCircle2Icon,
  SendIcon,
  DropletsIcon,
  FlaskConicalIcon,
  ShieldCheckIcon,
} from "lucide-react";

const CHECK_ITEMS = [
  { key: "lavagem", icon: DropletsIcon, label: "Lavagem & Descontaminação concluída" },
  { key: "estanqueidade", icon: FlaskConicalIcon, label: "Teste de Estanqueidade (vazamento) OK" },
  { key: "estrutura", icon: ShieldCheckIcon, label: "Válvulas & estrutura sem avarias" },
];

export default function Manutencao() {
  const [items, setItems] = useState([]);
  const [returnSerie, setReturnSerie] = useState("");
  const [drafts, setDrafts] = useState({}); // per-serie: {lavagem, estanqueidade, estrutura, proxima_inspecao, historico_reparo}
  const [transferOpen, setTransferOpen] = useState(null);
  const [transferForm, setTransferForm] = useState({ destino: "", manifesto: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/containers", { params: { status: "EM_MANUTENCAO" } });
    setItems(data);
    const next = {};
    data.forEach((c) => {
      next[c.no_serie] = {
        lavagem: !!c.checklist_lavagem,
        estanqueidade: !!c.checklist_estanqueidade,
        estrutura: !!c.checklist_estrutura,
        proxima_inspecao: c.proxima_inspecao || "",
        historico_reparo: c.historico_reparo || "",
      };
    });
    setDrafts(next);
  };

  useEffect(() => { load(); }, []);

  const onReceive = async (e) => {
    e.preventDefault();
    if (!returnSerie.trim()) return;
    setBusy(true);
    try {
      await api.post(`/containers/${returnSerie.trim()}/retornar`);
      toast.success("Retorno registrado — contentor enviado para manutenção");
      setReturnSerie("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const setCheck = (no_serie, key, value) => {
    setDrafts((d) => ({
      ...d,
      [no_serie]: { ...d[no_serie], [key]: !!value },
    }));
  };

  const setProxima = (no_serie, value) =>
    setDrafts((d) => ({ ...d, [no_serie]: { ...d[no_serie], proxima_inspecao: value } }));

  const setHistorico = (no_serie, value) =>
    setDrafts((d) => ({ ...d, [no_serie]: { ...d[no_serie], historico_reparo: value } }));

  const saveChecklist = async (no_serie) => {
    const d = drafts[no_serie] || {};
    try {
      await api.post(`/containers/${no_serie}/checklist`, {
        lavagem: !!d.lavagem,
        estanqueidade: !!d.estanqueidade,
        estrutura: !!d.estrutura,
        proxima_inspecao: d.proxima_inspecao || null,
      });
      toast.success("Checklist salvo");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const disponibilizar = async (no_serie) => {
    const d = drafts[no_serie] || {};
    if (!(d.historico_reparo && d.historico_reparo.trim())) {
      return toast.error("Descreva o Histórico de Reparo (o que foi consertado) antes de liberar");
    }
    try {
      // Save checklist first to make sure backend has latest state
      await saveChecklist(no_serie);
      await api.post(`/containers/${no_serie}/disponibilizar`, { historico_reparo: d.historico_reparo.trim() });
      toast.success(`${no_serie} liberado para envase`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const submitTransfer = async () => {
    if (!transferOpen) return;
    if (!transferForm.destino || !transferForm.manifesto) {
      return toast.error("Preencha destino e manifesto");
    }
    setBusy(true);
    try {
      await saveChecklist(transferOpen);
      await api.post(`/containers/${transferOpen}/transferir`, transferForm);
      toast.success(`${transferOpen} enviado para ${transferForm.destino}`);
      setTransferOpen(null);
      setTransferForm({ destino: "", manifesto: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1600px] mx-auto">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-1">Logística Reversa · Manutenção</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold leading-none">Triagem &amp; Manutenção</h1>
          <p className="text-sm text-muted-foreground mt-2">Bipa o retorno, executa checklist técnico e decide o destino do ativo.</p>
        </div>
        <WrenchIcon className="w-12 h-12 text-[#FF9F0A]" strokeWidth={1.2} />
      </header>

      {/* Return scan */}
      <Card className="bg-[#14151A] border-border rounded-none p-6 mb-6 border-l-4 border-l-[#FF9F0A]">
        <div className="flex items-center gap-2 mb-3">
          <ArrowDownToLineIcon className="w-5 h-5 text-[#FF9F0A]" />
          <h2 className="font-display text-2xl font-bold">Bipagem de retorno (cliente → base)</h2>
        </div>
        <form onSubmit={onReceive} className="flex gap-3 items-end">
          <div className="flex-1">
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">N° Série do contentor retornando</Label>
            <div className="relative mt-1">
              <ScanLineIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#FFD60A]" />
              <Input
                data-testid="manut-retorno-serie"
                value={returnSerie}
                onChange={(e) => setReturnSerie(e.target.value.toUpperCase())}
                placeholder="Bipa ou digite (ex: TQ-5000-1001)"
                autoFocus
                className="pl-11 rounded-none bg-[#0A0A0C] font-mono h-14 text-lg border-l-4 border-l-[#FF9F0A]"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={busy || !returnSerie}
            data-testid="manut-retorno-submit"
            className="h-14 px-8 rounded-none bg-[#FF9F0A] text-black hover:bg-[#FF9F0A]/85 font-bold uppercase tracking-wider"
          >
            Registrar Retorno
          </Button>
        </form>
      </Card>

      {/* Items in maintenance */}
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">Em manutenção · {items.length} ativo(s)</h2>
        <div className="text-xs text-muted-foreground">Marque os 3 itens do checklist para liberar as ações</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {items.length === 0 && (
          <Card className="lg:col-span-2 bg-[#14151A] border-border rounded-none p-8 text-center text-muted-foreground">
            Nenhum contentor em manutenção. Bipa um retorno acima para iniciar.
          </Card>
        )}
        {items.map((c) => {
          const d = drafts[c.no_serie] || {};
          const allChecked = d.lavagem && d.estanqueidade && d.estrutura;
          return (
            <Card
              key={c.no_serie}
              data-testid={`manut-card-${c.no_serie}`}
              className={`bg-[#14151A] border-border rounded-none p-5 border-l-2 ${allChecked ? "border-l-[#34C759]" : "border-l-[#FF9F0A]"}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="font-mono text-2xl font-bold text-[#FFD60A]">{c.no_serie}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {c.tipo_armazenamento} · {c.capacidade}L · Tara {c.tara}kg
                  </div>
                  {c.data_retorno && (
                    <div className="text-[11px] text-muted-foreground font-mono mt-1">
                      Retorno: {new Date(c.data_retorno).toLocaleString("pt-BR")}
                    </div>
                  )}
                </div>
                <Badge className="rounded-none border bg-[#FF9F0A]/15 text-[#FF9F0A] border-[#FF9F0A]/40">
                  EM MANUTENÇÃO
                </Badge>
              </div>

              {/* Checklist */}
              <div className="space-y-2 mb-4">
                {CHECK_ITEMS.map(({ key, icon: Icon, label }) => {
                  const checked = !!d[key];
                  return (
                    <div
                      key={key}
                      className={`flex items-center gap-3 border p-3 transition-colors ${
                        checked ? "border-[#34C759]/60 bg-[#34C759]/5" : "border-border bg-[#0A0A0C]"
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => setCheck(c.no_serie, key, v)}
                        data-testid={`manut-check-${c.no_serie}-${key}`}
                        className="rounded-none border-border data-[state=checked]:bg-[#34C759] data-[state=checked]:text-black"
                      />
                      <Icon className={`w-4 h-4 ${checked ? "text-[#34C759]" : "text-muted-foreground"}`} />
                      <span className={`text-sm ${checked ? "text-white" : "text-muted-foreground"}`}>{label}</span>
                    </div>
                  );
                })}
              </div>

              {/* Motivo da reprovação (só leitura) */}
              {c.motivo_reprovacao && (
                <div className="mb-3 border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-2 text-[11px] text-[#FF3B30]" data-testid={`motivo-${c.no_serie}`}>
                  <span className="font-bold uppercase tracking-wider">Reprovado por: </span>{c.motivo_reprovacao}
                </div>
              )}

              {/* Histórico de Reparo */}
              <div className="mb-4">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Histórico de Reparo · o que foi consertado *</Label>
                <textarea
                  value={d.historico_reparo || ""}
                  onChange={(e) => setHistorico(c.no_serie, e.target.value)}
                  data-testid={`manut-historico-${c.no_serie}`}
                  rows={3}
                  placeholder="Ex: substituição da válvula superior, troca da vedação, lavagem com solvente..."
                  className="mt-1 w-full rounded-none bg-[#0A0A0C] border border-border px-3 py-2 text-sm font-mono resize-none focus:outline-none focus:border-[#FFD60A]"
                />
              </div>

              {/* Próxima inspeção */}
              <div className="mb-4">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Próxima inspeção / validade do teste</Label>
                <Input
                  type="date"
                  value={d.proxima_inspecao || ""}
                  onChange={(e) => setProxima(c.no_serie, e.target.value)}
                  data-testid={`manut-proxima-${c.no_serie}`}
                  className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10"
                />
              </div>

              {/* Actions */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => disponibilizar(c.no_serie)}
                  disabled={!allChecked || !(d.historico_reparo && d.historico_reparo.trim())}
                  data-testid={`manut-disponibilizar-${c.no_serie}`}
                  className="rounded-none bg-[#34C759] text-black hover:bg-[#34C759]/85 font-bold uppercase tracking-wider text-xs h-11"
                >
                  <CheckCircle2Icon className="w-4 h-4 mr-1" />
                  Disponibilizar p/ Envase
                </Button>
                <Button
                  onClick={() => setTransferOpen(c.no_serie)}
                  disabled={!allChecked}
                  data-testid={`manut-transferir-${c.no_serie}`}
                  variant="outline"
                  className="rounded-none border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 font-bold uppercase tracking-wider text-xs h-11"
                >
                  <SendIcon className="w-4 h-4 mr-1" />
                  Transferir Vazio
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Transfer modal */}
      <Dialog open={!!transferOpen} onOpenChange={(o) => !o && setTransferOpen(null)}>
        <DialogContent className="bg-[#14151A] border-border rounded-none max-w-lg" data-testid="transfer-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Transferir vazio</DialogTitle>
            <div className="text-xs text-muted-foreground mt-1">
              Contentor: <span className="font-mono text-[#FFD60A]">{transferOpen}</span>
            </div>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Local de Destino / Filial</Label>
              <Input
                data-testid="transfer-destino"
                value={transferForm.destino}
                onChange={(e) => setTransferForm({ ...transferForm, destino: e.target.value })}
                placeholder="Filial Campinas"
                className="mt-1 rounded-none bg-[#0A0A0C] h-10"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">N° Manifesto de Transporte</Label>
              <Input
                data-testid="transfer-manifesto"
                value={transferForm.manifesto}
                onChange={(e) => setTransferForm({ ...transferForm, manifesto: e.target.value })}
                placeholder="MAN-2026-001"
                className="mt-1 rounded-none bg-[#0A0A0C] font-mono h-10"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(null)} className="rounded-none border-border">
              Cancelar
            </Button>
            <Button
              onClick={submitTransfer}
              disabled={busy}
              data-testid="transfer-confirm"
              className="rounded-none bg-[#00E5FF] text-black hover:bg-[#00E5FF]/85 font-bold uppercase tracking-wider"
            >
              Confirmar transferência
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
